/**
 * 🔧 Environment Variables — Validated at Startup
 *
 * يستخدم Zod للتحقق من كل env var قبل تشغيل التطبيق.
 * لو في أي var ناقص → التطبيق يفشل فوراً برسالة واضحة.
 */

import { z } from 'zod';

const envSchema = z.object({
  // Firebase
  VITE_FIREBASE_API_KEY: z.string().min(1, 'Firebase API Key مطلوب'),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),

  /**
   * 🔔 مفتاح VAPID العام (Web Push).
   *    اختياري — بدونه يظل Web Push معطّلاً (لكن الجرس والصوت يعملان).
   *    يُولّد من Firebase Console → Cloud Messaging → Web Push certificates.
   */
  VITE_FIREBASE_VAPID_KEY: z.string().optional().default(''),

  // App
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_USE_FIREBASE_EMULATOR: z
    .string()
    .transform((val) => val === 'true')
    .default('false'),
  // Features
  VITE_ENABLE_PHONE_AUTH: z
    .string()
    .transform((val) => val === 'true')
    .default('true'),
  VITE_ENABLE_EMAIL_AUTH: z
    .string()
    .transform((val) => val === 'true')
    .default('true'),

  /**
   * 💳 تفعيل ميزات الدفع/العمولة (deposit / commission / revenue).
   *
   * 🔄 نموذج العمل الحالي (lead-gen / MVP): الدفع يتم خارج التطبيق،
   *    فالميزة معطّلة افتراضياً. الكود يبقى موجوداً ويُعاد تفعيله
   *    عند رجوع رسوم المنصة لاحقاً (مجرد قلب القيمة لـ 'true').
   */
  VITE_ENABLE_PAYMENTS: z
    .string()
    .transform((val) => val === 'true')
    .default('false'),
});

function loadEnv() {
  try {
    return envSchema.parse(import.meta.env);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const issues = error.errors
        .map((e) => `  ❌ ${e.path.join('.')}: ${e.message}`)
        .join('\n');
      throw new Error(
        `\n🚨 Environment Variables غير صحيحة:\n\n${issues}\n\n` +
          `💡 تأكد من ملف .env.local — انسخ من .env.example`
      );
    }
    throw error;
  }
}

export const env = loadEnv();

export const isDev = env.VITE_APP_ENV === 'development';
export const isProd = env.VITE_APP_ENV === 'production';

/**
 * 💳 هل ميزات الدفع/العمولة/الإيرادات مفعّلة؟
 *
 * استخدمها لإخفاء أي UI متعلّق بالمبالغ (عربون، عمولة، إيرادات) في نموذج lead-gen.
 * البيانات تبقى محفوظة في Firestore — فقط لا تُعرض.
 */
export const paymentsEnabled = env.VITE_ENABLE_PAYMENTS;
